// Literal parsers for loop expression operands.
// Example: scope value `12px` or `'name'` is normalized here.
import { stripWrappingQuotes } from './selector'
import type { LoopExpressionValue } from './placeholder-expression-types'

// Parse one scope-provided raw value into numeric or string form.
export function parseScopeValue(value: string): LoopExpressionValue | null {
  const stripped = stripWrappingQuotes(value.trim())
  const numeric = parseNumericValue(stripped)

  // Return numeric scope values when parsing succeeds.
  if (numeric) {
    // Return the parsed numeric scope value.
    return numeric
  }

  // Return the raw scope value as a string operand.
  return { kind: 'string', value: stripped }
}

// Parse one literal numeric token with optional unit suffix.
export function parseNumericValue(token: string): LoopExpressionValue | null {
  let index = token[0] === '-' ? 1 : 0
  let sawDigit = false
  let sawDot = false

  // Consume the numeric body before the unit suffix.
  while (index < token.length) {
    const char = token[index]
    if (char >= '0' && char <= '9') {
      sawDigit = true
      index += 1
      continue
    }
    if (char === '.' && !sawDot) {
      sawDot = true
      index += 1
      continue
    }
    break
  }

  // Stop when the token did not contain numeric digits.
  if (!sawDigit) {
    // Return null for non-numeric literal tokens.
    return null
  }

  const numericValue = Number.parseFloat(token.slice(0, index))

  // Stop when the numeric literal cannot be parsed.
  if (Number.isNaN(numericValue)) {
    // Return null for malformed numeric values.
    return null
  }

  // Return the parsed numeric literal value and unit suffix.
  return { kind: 'number', value: numericValue, unit: token.slice(index) }
}
